import { AccountCreateError } from 'utils/constants/accounts';
import { SortType } from 'utils/constants/common';
import { getTomorrowDate } from 'utils/date';

import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import { Balance } from 'store/models/balance';

type AccountUsers = string[];
type AccountTitle = string;
export type AccountId = number | null | undefined;

export interface User {
    id: number;
    name: string;
    email: string;
}

export interface Account {
    createdAt: string;
    expiredDate: string;
    id: number;
    title: string;
    users: User[];
}

export interface AccountCreateInfo {
    title: string;
    userIds: string[];
    id?: AccountId;
    expiredDate?: string | null;
    pricePerClickInCents?: number | null;
}

interface Sort {
    key: keyof Account;
    sort: SortType;
}

// ----------------------- actions -------------------------------------
const SET_ACCOUNTS_ACCOUNTS = 'SET_ACCOUNTS_ACCOUNTS';
const SET_ACCOUNTS_BALANCE = 'SET_ACCOUNTS_BALANCE';
const SET_ACCOUNTS_EMPLOYER_USERS = 'SET_ACCOUNTS_EMPLOYER_USERS';
const SET_ACCOUNTS_ACCOUNT_USERS = 'SET_ACCOUNTS_ACCOUNT_USERS';
const SET_ACCOUNTS_ACCOUNT_TITLE = 'SET_ACCOUNTS_ACCOUNT_TITLE';
const SET_ACCOUNTS_ACCOUNT_ID = 'SET_ACCOUNTS_ACCOUNT_ID';
const SET_CREATE_ACCOUNT_ERRORS = 'SET_CREATE_ACCOUNT_ERRORS';
const SET_ACCOUNTS_SORTS = 'SET_ACCOUNTS_SORTS';
const SET_ACCOUNTS_EXPIRED_DATE = 'SET_ACCOUNTS_EXPIRED_DATE';
const SET_ACCOUNTS_PRICE_PER_CLICK = 'SET_ACCOUNTS_PRICE_PER_CLICK';
const SET_ACCOUNTS_IS_CONTRACT_SETTINGS = 'SET_ACCOUNTS_IS_CONTRACT_SETTINGS';
const SET_ACCOUNTS_IS_CONTRACT_EXIST = 'SET_ACCOUNTS_IS_CONTRACT_EXIST';
const RESET_ACCOUNTS_ACCOUNT_INFO = 'RESET_ACCOUNTS_ACCOUNT_INFO';

interface PayloadTypes {
    [SET_ACCOUNTS_ACCOUNTS]: Account[];
    [SET_ACCOUNTS_BALANCE]: Record<string, Balance>;
    [SET_ACCOUNTS_EMPLOYER_USERS]: User[];
    [SET_ACCOUNTS_ACCOUNT_USERS]: AccountUsers;
    [SET_ACCOUNTS_ACCOUNT_TITLE]: AccountTitle;
    [SET_ACCOUNTS_ACCOUNT_ID]: AccountId;
    [SET_CREATE_ACCOUNT_ERRORS]: AccountCreateError[];
    [SET_ACCOUNTS_SORTS]: Sort[];
    [SET_ACCOUNTS_EXPIRED_DATE]: Date;
    [SET_ACCOUNTS_PRICE_PER_CLICK]: number | null;
    [SET_ACCOUNTS_IS_CONTRACT_SETTINGS]: boolean;
    [SET_ACCOUNTS_IS_CONTRACT_EXIST]: boolean;
    [RESET_ACCOUNTS_ACCOUNT_INFO]: null;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setAccountsAccounts = ActionCreator(SET_ACCOUNTS_ACCOUNTS);
export const setAccountsBalance = ActionCreator(SET_ACCOUNTS_BALANCE);
export const setAccountsEmployerUsers = ActionCreator(SET_ACCOUNTS_EMPLOYER_USERS);
export const setAccountsAccountUsers = ActionCreator(SET_ACCOUNTS_ACCOUNT_USERS);
export const setAccountsAccountTitle = ActionCreator(SET_ACCOUNTS_ACCOUNT_TITLE);
export const setAccountsAccountId = ActionCreator(SET_ACCOUNTS_ACCOUNT_ID);
export const setCreateAccountErrors = ActionCreator(SET_CREATE_ACCOUNT_ERRORS);
export const setAccountsSorts = ActionCreator(SET_ACCOUNTS_SORTS);
export const setAccountsExpiredDate = ActionCreator(SET_ACCOUNTS_EXPIRED_DATE);
export const setAccountsPricePerClick = ActionCreator(SET_ACCOUNTS_PRICE_PER_CLICK);
export const setAccountsIsContractSettings = ActionCreator(SET_ACCOUNTS_IS_CONTRACT_SETTINGS);
export const setAccountsIsContractExist = ActionCreator(SET_ACCOUNTS_IS_CONTRACT_EXIST);
export const resetAccountsAccountInfo = ActionCreator(RESET_ACCOUNTS_ACCOUNT_INFO);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface AccountsState {
    accounts: Account[];
    balance: Record<string, Balance>;
    employerUsers: User[];
    accountUsers: AccountUsers;
    accountTitle: AccountTitle;
    accountId: AccountId;
    accountErrors: AccountCreateError[];
    sorts: Sort[];
    expiredDate: Date;
    pricePerClick: number | null;
    isContractSettings: boolean;
    isContractExist: boolean;
}

const ACCOUNTS_INITIAL_STATE: AccountsState = {
    accounts: [],
    balance: {},
    employerUsers: [],
    accountUsers: [],
    accountTitle: '',
    accountId: null,
    accountErrors: [],
    sorts: [],
    expiredDate: getTomorrowDate(),
    pricePerClick: null,
    isContractSettings: false,
    isContractExist: false,
};

export default createReducer<AccountsState, PayloadTypes>(ACCOUNTS_INITIAL_STATE, {
    [SET_ACCOUNTS_ACCOUNTS]: (state, { payload }) => ({
        ...state,
        accounts: payload,
    }),
    [SET_ACCOUNTS_BALANCE]: (state, { payload }) => ({
        ...state,
        balance: payload,
    }),
    [SET_ACCOUNTS_EMPLOYER_USERS]: (state, { payload }) => ({
        ...state,
        employerUsers: payload,
    }),
    [SET_ACCOUNTS_ACCOUNT_USERS]: (state, { payload }) => ({
        ...state,
        accountUsers: payload,
    }),
    [SET_ACCOUNTS_ACCOUNT_TITLE]: (state, { payload }) => ({
        ...state,
        accountTitle: payload,
    }),
    [SET_ACCOUNTS_ACCOUNT_ID]: (state, { payload }) => ({
        ...state,
        accountId: payload,
    }),
    [SET_CREATE_ACCOUNT_ERRORS]: (state, { payload }) => ({
        ...state,
        accountErrors: payload,
    }),
    [SET_ACCOUNTS_SORTS]: (state, { payload }) => ({
        ...state,
        sorts: payload,
    }),
    [SET_ACCOUNTS_EXPIRED_DATE]: (state, { payload }) => ({
        ...state,
        expiredDate: payload,
    }),
    [SET_ACCOUNTS_PRICE_PER_CLICK]: (state, { payload }) => ({
        ...state,
        pricePerClick: payload,
    }),
    [SET_ACCOUNTS_IS_CONTRACT_SETTINGS]: (state, { payload }) => ({
        ...state,
        isContractSettings: payload,
    }),
    [SET_ACCOUNTS_IS_CONTRACT_EXIST]: (state, { payload }) => ({
        ...state,
        isContractExist: payload,
    }),
    [RESET_ACCOUNTS_ACCOUNT_INFO]: (state) => ({
        ...state,
        accountUsers: [],
        accountTitle: '',
        accountId: null,
        accountErrors: [],
    }),
});
// ----------------------- /reducer --------------------------------------
